// Settings → Catalogues (Products / Work Sources / Payment Modes) — Wave E.
//
// Single component, three routes. Behaviour identical across the three:
//   - list view with rows
//   - "Add" inline form (name + description + display_order + active)
//   - inline edit per row
//   - delete with confirmation modal
//
// Backed by the new admin CRUD endpoints under /products, /work-sources,
// /payment-modes (see server/src/modules/reference-data/catalogues.router).

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Table, type Column, type RowAction } from '../../../components/ui/Table/Table';
import { Badge } from '../../../components/ui/Badge/Badge';
import { Input } from '../../../components/form/Input';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import {
  useCatalogue,
  useCreateCatalogueItem,
  useDeleteCatalogueItem,
  useUpdateCatalogueItem,
} from './hooks/useCatalogue';
import type { CatalogueKind } from '../../../api/catalogues';
import type { ReferenceItem } from '../../../types/api';
import styles from './CataloguePage.module.css';

interface CataloguePageProps {
  kind: CatalogueKind;
  title: string;
  subtitle: string;
  itemLabel: string;
}

export function CataloguePage({
  kind,
  title,
  subtitle,
  itemLabel,
}: CataloguePageProps): JSX.Element {
  const navigate = useNavigate();
  const list = useCatalogue(kind);
  const create = useCreateCatalogueItem(kind);
  const update = useUpdateCatalogueItem(kind);
  const remove = useDeleteCatalogueItem(kind);

  const items = list.data ?? [];

  // New-item form
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newOrder, setNewOrder] = useState<number>(items.length);

  // Editing state
  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editOrder, setEditOrder] = useState<number>(0);
  const [editActive, setEditActive] = useState<boolean>(true);

  const [deleteTarget, setDeleteTarget] = useState<ReferenceItem | null>(null);

  function startEdit(row: ReferenceItem) {
    setEditId(row.id);
    setEditName(row.name);
    setEditDesc(row.description ?? '');
    setEditOrder(row.display_order);
    setEditActive(row.is_active);
  }

  function cancelEdit() {
    setEditId(null);
  }

  function handleCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!newName.trim()) return;
    create.mutate(
      {
        name: newName.trim(),
        description: newDesc.trim() || null,
        display_order: newOrder,
        is_active: true,
      },
      {
        onSuccess: () => {
          setNewName('');
          setNewDesc('');
          setNewOrder(items.length + 1);
        },
      },
    );
  }

  function handleSaveEdit() {
    if (editId === null) return;
    update.mutate(
      {
        id: editId,
        input: {
          name: editName.trim(),
          description: editDesc.trim() || null,
          display_order: editOrder,
          is_active: editActive,
        },
      },
      { onSuccess: () => setEditId(null) },
    );
  }

  const columns: Column<ReferenceItem>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (_v, row) =>
        editId === row.id ? (
          <Input
            aria-label={`Edit name for ${row.name}`}
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
          />
        ) : (
          row.name
        ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (_v, row) =>
        editId === row.id ? (
          <Input
            aria-label={`Edit description for ${row.name}`}
            value={editDesc}
            onChange={(e) => setEditDesc(e.target.value)}
          />
        ) : (
          row.description ?? '—'
        ),
    },
    {
      key: 'display_order',
      header: 'Order',
      align: 'right',
      render: (_v, row) =>
        editId === row.id ? (
          <Input
            aria-label={`Edit display order for ${row.name}`}
            variant="number"
            value={editOrder}
            onChange={(e) => setEditOrder(Number(e.target.value))}
          />
        ) : (
          row.display_order
        ),
    },
    {
      key: 'is_active',
      header: 'Active',
      render: (_v, row) =>
        editId === row.id ? (
          <label className={styles.activeToggle}>
            <input
              type="checkbox"
              checked={editActive}
              onChange={(e) => setEditActive(e.target.checked)}
              aria-label={`Toggle active for ${row.name}`}
            />
            <span>Active</span>
          </label>
        ) : row.is_active ? (
          <Badge variant="success">Active</Badge>
        ) : (
          <Badge variant="muted">Inactive</Badge>
        ),
    },
  ];

  const rowActions = (row: ReferenceItem): RowAction<ReferenceItem>[] =>
    editId === row.id
      ? [
          {
            label: 'Save',
            icon: Save,
            onClick: handleSaveEdit,
            variant: 'primary',
          },
          {
            label: 'Cancel',
            icon: X,
            onClick: cancelEdit,
            variant: 'ghost',
          },
        ]
      : [
          {
            label: 'Edit',
            icon: Pencil,
            onClick: () => startEdit(row),
            variant: 'ghost',
          },
          {
            label: 'Delete',
            icon: Trash2,
            onClick: () => setDeleteTarget(row),
            variant: 'danger',
          },
        ];

  return (
    <div className={styles.page}>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate('/settings')}
          >
            Back
          </Button>
        }
      />

      <Card title={`Add ${itemLabel}`}>
        <form onSubmit={handleCreate} className={styles.addForm}>
          <Input
            label="Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            required
          />
          <Input
            label="Description"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
          />
          <Input
            label="Display Order"
            variant="number"
            value={newOrder}
            onChange={(e) => setNewOrder(Number(e.target.value))}
          />
          <Button type="submit" variant="primary" icon={Plus} loading={create.isPending}>
            Add {itemLabel}
          </Button>
        </form>
      </Card>

      <Card flush>
        <Table
          columns={columns}
          data={items}
          keyField={(row) => row.id}
          loading={list.isLoading}
          emptyState={
            <div className={styles.empty}>
              <h3>No {title.toLowerCase()} yet</h3>
              <p>Use the form above to add your first one.</p>
            </div>
          }
          rowActions={rowActions}
          ariaLabel={title}
        />
      </Card>

      <ConfirmModal
        open={deleteTarget !== null}
        title={`Delete ${itemLabel.toLowerCase()}?`}
        message={
          deleteTarget !== null
            ? `Delete "${deleteTarget.name}"? This is a soft delete — existing bookings/receipts that reference it remain valid.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={remove.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget === null) return;
          remove.mutate(deleteTarget.id, {
            onSuccess: () => {
              setDeleteTarget(null);
              void list.refetch();
            },
          });
        }}
      />
    </div>
  );
}

// -- Per-route wrappers --------------------------------------------------

export function ProductsPage(): JSX.Element {
  return (
    <CataloguePage
      kind="products"
      title="Products"
      subtitle="The catalogue of services / vehicle types you offer"
      itemLabel="Product"
    />
  );
}

export function WorkSourcesPage(): JSX.Element {
  return (
    <CataloguePage
      kind="workSources"
      title="Work Sources"
      subtitle="Where bookings come from (websites, partners, referrals)"
      itemLabel="Work Source"
    />
  );
}

export function PaymentModesPage(): JSX.Element {
  return (
    <CataloguePage
      kind="paymentModes"
      title="Payment Modes"
      subtitle="Accepted payment methods (cash, BACS, cheque, etc.)"
      itemLabel="Payment Mode"
    />
  );
}
